import type { AttachmentPdfSignaturePlacement } from '@blocksuite/affine/model';
import { CloseIcon } from '@blocksuite/icons/rc';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';

import * as styles from './styles.css';

export interface PdfVisualSignatureOverlayProps {
  placements: AttachmentPdfSignaturePlacement[];
  pageIndex: number;
  appearanceImageUrl: (appearanceId: string) => string;
  interactive?: boolean;
  selectedPlacementId?: string | null;
  removeLabel?: string;
  onPlacementPointerDown?: (
    event: ReactPointerEvent<HTMLElement>,
    placement: AttachmentPdfSignaturePlacement
  ) => void;
  onPlacementPointerMove?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPlacementPointerUp?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPlacementPointerCancel?: (event: ReactPointerEvent<HTMLElement>) => void;
  onPlacementKeyDown?: (
    event: ReactKeyboardEvent<HTMLDivElement>,
    placement: AttachmentPdfSignaturePlacement
  ) => void;
  onResizePointerDown?: (
    event: ReactPointerEvent<HTMLElement>,
    placement: AttachmentPdfSignaturePlacement
  ) => void;
  onRemove?: (placement: AttachmentPdfSignaturePlacement) => void;
}

export function PdfVisualSignatureOverlay({
  placements,
  pageIndex,
  appearanceImageUrl,
  interactive = false,
  selectedPlacementId = null,
  removeLabel = 'Remove signature',
  onPlacementPointerDown,
  onPlacementPointerMove,
  onPlacementPointerUp,
  onPlacementPointerCancel,
  onPlacementKeyDown,
  onResizePointerDown,
  onRemove,
}: PdfVisualSignatureOverlayProps) {
  const pagePlacements = placements.filter(
    placement => placement.pageIndex === pageIndex
  );
  if (!pagePlacements.length) return null;

  return (
    <div className={styles.pdfSignatureOverlay} aria-hidden={!interactive}>
      {pagePlacements.map(placement => {
        const selected = interactive && selectedPlacementId === placement.id;
        return (
          <div
            key={placement.id}
            className={styles.pdfSignaturePlacement}
            data-pdf-signature-placement-id={placement.id}
            data-read-only={!interactive ? 'true' : undefined}
            data-selected={selected ? 'true' : undefined}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            aria-label={interactive ? 'PDF signature placement' : undefined}
            style={{
              left: `${placement.rect.x * 100}%`,
              top: `${placement.rect.y * 100}%`,
              width: `${placement.rect.width * 100}%`,
              height: `${placement.rect.height * 100}%`,
              transform: placement.rotation
                ? `rotate(${placement.rotation}deg)`
                : undefined,
            }}
            onPointerDown={
              interactive
                ? event => onPlacementPointerDown?.(event, placement)
                : undefined
            }
            onPointerMove={interactive ? onPlacementPointerMove : undefined}
            onPointerUp={interactive ? onPlacementPointerUp : undefined}
            onPointerCancel={interactive ? onPlacementPointerCancel : undefined}
            onKeyDown={
              interactive
                ? event => onPlacementKeyDown?.(event, placement)
                : undefined
            }
          >
            {placement.signatureAppearanceId ? (
              <img
                alt={interactive ? 'Signature appearance' : ''}
                className={styles.pdfSignaturePlacementImage}
                src={appearanceImageUrl(placement.signatureAppearanceId)}
              />
            ) : null}
            {selected && onRemove ? (
              <button
                type="button"
                className={styles.pdfSignaturePlacementDeleteButton}
                aria-label={removeLabel}
                onClick={event => {
                  event.stopPropagation();
                  onRemove(placement);
                }}
                onPointerDown={event => event.stopPropagation()}
              >
                <CloseIcon aria-hidden />
              </button>
            ) : null}
            {interactive ? (
              <span
                className={styles.pdfSignatureResizeHandle}
                aria-hidden="true"
                onPointerDown={event => onResizePointerDown?.(event, placement)}
                onPointerMove={onPlacementPointerMove}
                onPointerUp={onPlacementPointerUp}
                onPointerCancel={onPlacementPointerCancel}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
